import {
  ApiRef,
  ConfigApi,
  createApiRef,
  DiscoveryApi,
} from '@backstage/core-plugin-api';
import { AxiosInstance } from 'axios';
import { AxiosInstanceProviderApi } from './axiosInstanceApi';

const DEFAULT_PROXY_PATH_BASE = '';

type Options = {
  discoveryApi: DiscoveryApi;
  /**
   * Path to use for requests via the proxy, defaults to ''
   */
  proxyPathBase?: string;
  configApi: ConfigApi;
  axiosInstanceProviderApi: AxiosInstanceProviderApi;
};
export interface GitHubCostCenterApi {
  getUsersList: (
    enterpriseName: string,
    user?: string,
    year?: string,
    month?: string,
  ) => Promise<any>;
  getCostCenters: (
    enterpriseName: string,
    user?: string,
    year?: string,
    month?: string,
  ) => Promise<any>;
  updateBudget: (
    enterpriseName: string,
    budgetId: string,
    billingApprover: string,
    budget_amount: number,
  ) => Promise<any>;
  updateDesignMetrics(data: any, token?: string): Promise<any>;
}

export const gitHubCostCenterRef = createApiRef<GitHubCostCenterApi>({
  id: 'cbre.devx.api.githubcostcenter',
});

export class GitHubCostCenterApiImpl implements GitHubCostCenterApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly proxyPathBase: string;
  private readonly axiosInstancePromise: Promise<AxiosInstance>;
  private coreURL: string = 'service/metrics/ai-credit-usage';
  private costcenterUrl: string = 'service/metrics/ai-credit-cost-centers';
  private authorizationKey = '';
  apiToken: string = '';
  apiBaseUrl: string = '';

  constructor(options: Options) {
    this.discoveryApi = options.discoveryApi;
    this.proxyPathBase = options.proxyPathBase || DEFAULT_PROXY_PATH_BASE;
    this.axiosInstancePromise = options.axiosInstanceProviderApi.getInstance();
  }

  private async getApiUrl() {
    const baseUrl = await this.discoveryApi.getBaseUrl('proxy');
    return `${baseUrl}${this.proxyPathBase}/figma`;
  }

  public isAuthorizationKeySet(): boolean {
    return this.authorizationKey !== '';
  }

  public setAuthorizationKey(authorizationKey: string) {
    this.authorizationKey = `Basic ${authorizationKey}`;
  }

  getHeaders = (
    profEmail: string,
    profileName: string,
    authorizationKey: string | null,
  ) => {
    return {
      'Content-Type': 'application/json',
      Authorization: authorizationKey || this.authorizationKey,
      'X-User-Email': profEmail,
      'X-User-Name': profileName,
    };
  };

  async getUsersList(
    enterpriseName: string,
    user?: string,
    year?: string,
    month?: string,
  ): Promise<any> {
    const instance = await this.axiosInstancePromise;
    let subPath = '';
    if (user) subPath += `/${user}`;
    if (year) subPath += `/${year}`;
    if (month) subPath += `/${month}`;
    return instance
      .get(`${this.coreURL}/${enterpriseName}${subPath}`)
      .then(res => res.data);
  }
  async getCostCenters(
    enterpriseName: string,
    user?: string,
    year?: string,
    month?: string,
  ): Promise<any> {
    const instance = await this.axiosInstancePromise;
    let subPath = '';
    if (user) subPath += `/${user}`;
    if (year) subPath += `/${year}`;
    if (month) subPath += `/${month}`;
    return instance
      .get(`${this.costcenterUrl}/${enterpriseName}${subPath}`)
      .then(res => res.data);
  }
  async updateBudget(
    enterpriseName: string,
    budgetId: string,
    billingApprover: string,
    budget_amount: number,
  ): Promise<any> {
    const instance = await this.axiosInstancePromise;
    const payload = {
      enterpriseName,
      budgetId,
      billingApprover,
      prevent_further_usage: false,
      budget_amount,
      budget_alerting: {
        will_alert: true,
        alert_recipients: ['jrblocker'],
      },
    };
    return instance
      .patch('service/github/budgets/update', payload)
      .then(res => res.data);
  }
  async updateDesignMetrics(data: any, token?: string): Promise<any> {
    const instance = await this.axiosInstancePromise;
    const headers = {
      'api-version': 1.0,
      clientid: '' + Date.now(),
      'Content-Type': 'application/json',
      Authorization: this.authorizationKey,
    };
    if (token) {
      headers['Authorization'] = `Basic ${token}`;
    }
    return instance
      .post(`/service/discovery/secure/metrics/logusage`, data, {
        headers,
      })
      .then(res => res.data);
  }
}
